import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { formatRubles } from "@velizhanin-dev/bot-scenario"
import { CreditCard } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import {
	type BotPayment,
	botBuilderKeys,
	type CustomBot,
	listBotPayments,
	removePaymentToken,
	setPaymentToken,
} from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { Card, CardContent } from "@/shared/ui/card"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { StatusBadge } from "@/shared/ui/status-badge"
import { AutoHeight } from "@/shared/ui/auto-height"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Skeleton } from "@/shared/ui/skeleton"

const RECENT_LIMIT = 10

const formatDateTime = (value: string) =>
	new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })

const rubles = (kopecks: number) => formatRubles(kopecks / 100)

function customerName(payment: BotPayment): string {
	const { customer } = payment
	const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(" ")
	if (name) return name
	if (customer?.username) return `@${customer.username}`
	return `id ${payment.telegramUserId}`
}

/** Оплата через ЮKassa: токен провайдера из @BotFather и последние платежи. */
export function BotPaymentsCard({
	bot,
	onBotChange,
}: {
	bot: CustomBot
	onBotChange: (next: CustomBot) => Promise<void>
}) {
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const [token, setToken] = useState("")
	const connected = !!bot.paymentTokenLast4

	const payments = useQuery({
		queryKey: botBuilderKeys.payments(bot.id),
		queryFn: () => listBotPayments(bot.id, "PAID"),
	})

	const save = useMutation({
		mutationFn: () => setPaymentToken(bot.id, token.trim()),
		onSuccess: async (next) => {
			await onBotChange(next)
			setToken("")
			toast.success(
				next.paymentTestMode ? "Оплата подключена в тестовом режиме" : "Оплата подключена",
			)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось сохранить токен")),
	})

	const items = payments.data?.items.slice(0, RECENT_LIMIT) ?? []
	const totals = payments.data?.totals

	return (
		<Card className="border-border/60">
			{/* Высота карточки идёт за содержимым: платежи догрузились, оплату подключили. */}
			<AutoHeight>
				<CardContent className="space-y-4 p-4">
					<div className="flex items-center gap-3">
						<span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-400/15 text-emerald-300">
							<CreditCard className="h-[18px] w-[18px]" />
						</span>
						<div className="min-w-0 flex-1">
							<p className="text-sm font-medium">Оплата через ЮKassa</p>
							<p className="text-xs text-muted-foreground">
								{connected
									? `Токен …${bot.paymentTokenLast4}${bot.paymentTestMode ? ", тестовый режим (деньги не списываются)" : ""}`
									: "Не подключена: блок «Счёт на оплату» не сработает"}
							</p>
						</div>
						<StatusBadge tone={!connected ? "muted" : bot.paymentTestMode ? "warning" : "success"}>
							{!connected ? "выкл." : bot.paymentTestMode ? "тест" : "боевой"}
						</StatusBadge>
					</div>

					<form
						onSubmit={(event) => {
							event.preventDefault()
							if (token.trim()) save.mutate()
						}}
					>
						<Field
							label={connected ? "Заменить токен провайдера" : "Токен провайдера"}
							htmlFor="bot-payment-token"
							hint="@BotFather → ваш бот → Payments → ЮKassa. Токен со словом TEST включает тестовые платежи."
						>
							<div className="flex gap-2">
								<Input
									id="bot-payment-token"
									autoComplete="off"
									spellCheck={false}
									placeholder="381764678:TEST:12345"
									value={token}
									onChange={(event) => setToken(event.target.value)}
								/>
								<Button type="submit" variant="outline" disabled={!token.trim() || save.isPending}>
									{connected ? "Заменить" : "Подключить"}
								</Button>
							</div>
						</Field>
					</form>

					<LoadSwap loading={payments.isLoading} id={items.length ? "list" : "empty"}>
						{payments.isLoading ? (
							<div aria-hidden="true" className="space-y-2">
								<Skeleton className="h-[62px] w-full rounded-xl" />
								<Skeleton className="h-10 w-full rounded-lg" />
							</div>
						) : (
							<div className="space-y-4">
								{totals && (totals.live.count > 0 || totals.test.count > 0) ? (
									<div className="grid grid-cols-2 gap-2">
										<div className="rounded-xl bg-muted/50 px-3 py-2">
											<p className="text-xs text-muted-foreground">Оплачено</p>
											<p className="font-semibold tabular-nums">{rubles(totals.live.amount)}</p>
											<p className="text-[11px] text-muted-foreground">
												{totals.live.count} платеж.
											</p>
										</div>
										<div className="rounded-xl bg-muted/50 px-3 py-2">
											<p className="text-xs text-muted-foreground">Тестовые</p>
											<p className="font-semibold tabular-nums">{rubles(totals.test.amount)}</p>
											<p className="text-[11px] text-muted-foreground">
												{totals.test.count} платеж.
											</p>
										</div>
									</div>
								) : null}

								{items.length > 0 ? (
									<ul className="divide-y divide-border/60">
										{items.map((payment) => (
											<li key={payment.id} className="flex items-center gap-3 py-2">
												<div className="min-w-0 flex-1">
													<p className="truncate text-sm">{payment.title}</p>
													<p className="truncate text-xs text-muted-foreground">
														{customerName(payment)} ·{" "}
														{formatDateTime(payment.paidAt ?? payment.createdAt)}
													</p>
												</div>
												{payment.testMode ? (
													<StatusBadge tone="warning" size="tag">
														тест
													</StatusBadge>
												) : null}
												<span className="text-sm font-medium tabular-nums">
													{rubles(payment.amount)}
												</span>
											</li>
										))}
									</ul>
								) : payments.isSuccess ? (
									<p className="text-xs text-muted-foreground">Оплат пока не было.</p>
								) : null}
							</div>
						)}
					</LoadSwap>

					{connected ? (
						<Button
							variant="ghost"
							size="sm"
							className="text-red-400"
							onClick={() =>
								confirm({
									title: "Отключить оплату?",
									message: "Новые счета бот выставлять не сможет. История платежей останется.",
									confirmText: "Отключить",
									onConfirm: async () => {
										try {
											await onBotChange(await removePaymentToken(bot.id))
											await queryClient.invalidateQueries({
												queryKey: botBuilderKeys.payments(bot.id),
											})
										} catch (error) {
											toast.error(extractApiErrorMessage(error, "Не удалось отключить оплату"))
										}
									},
								})
							}
						>
							Отключить оплату
						</Button>
					) : null}
				</CardContent>
			</AutoHeight>
		</Card>
	)
}
